import { useEffect } from 'react';

interface SEOConfig {
  title: string;
  description: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  canonical?: string;
  structuredData?: Record<string, unknown>;
}

export const useSEO = (config: SEOConfig) => {
  useEffect(() => {
    // Set title
    document.title = `${config.title}`;

    // Set meta tags
    setMetaTag('description', config.description);
    setMetaTag('keywords', config.keywords?.join(', ') || '');
    setMetaTag('author', config.author || 'Parakh');

    // Open Graph tags
    setMetaTag('og:title', config.title, 'property');
    setMetaTag('og:description', config.description, 'property');
    setMetaTag('og:type', config.type || 'website', 'property');
    if (config.image) {
      setMetaTag('og:image', config.image, 'property');
    }
    if (config.url) {
      setMetaTag('og:url', config.url, 'property');
    }

    // Twitter tags
    setMetaTag('twitter:title', config.title, 'name');
    setMetaTag('twitter:description', config.description, 'name');
    if (config.image) {
      setMetaTag('twitter:image', config.image, 'name');
    }

    // Canonical URL
    if (config.canonical) {
      setCanonicalURL(config.canonical);
    }

    // Structured data
    if (config.structuredData) {
      setStructuredData(config.structuredData);
    }

    // Scroll to top on page change
    window.scrollTo(0, 0);
  }, [config]);
};

const setMetaTag = (
  name: string,
  content: string,
  type: 'name' | 'property' = 'name'
) => {
  if (!content) return;

  let element = document.querySelector(
    `meta[${type}="${name}"]`
  ) as HTMLMetaElement;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(type, name);
    document.head.appendChild(element);
  }

  element.content = content;
};

const setCanonicalURL = (url: string) => {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;

  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }

  link.href = url;
};

const setStructuredData = (data: Record<string, unknown>) => {
  let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(data);
};
